import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { translate, TranslocoModule } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MessageService, SharedModule } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogService, DynamicDialogConfig, DynamicDialogModule } from 'primeng/dynamicdialog';
import { MessagesModule } from 'primeng/messages';
import { TooltipModule } from 'primeng/tooltip';
import { BehaviorSubject, filter } from 'rxjs';

import { IVR, IVROption } from '@pwp-common';

import { makePTableCols } from '../../../../common/p-table/make-p-table-cols';
import { IvrService } from '../../../../services/call/ivr/ivr.service';
import { ConfirmWithInputComponent } from '../../../generic/confirm-with-input/confirm-with-input.component';
import { DataTableModule } from '../../../generic/data-table/data-table.module';
import { ProgressSpinnerModule } from '../../progress-spinner/progress-spinner.module';
import { IVROptionEditorComponent } from '../editor/ivr-option-editor/ivr-option-editor.component';
import { createIVROptionEditorRow } from '../helpers/create-ivr-option-editor-row/create-ivr-option-editor-row';
import { IVROptionEditorRow } from '../helpers/create-ivr-option-editor-row/interfaces';

@UntilDestroy()
@Component({
  selector: 'app-ivr-option-table-editor',
  standalone: true,
  templateUrl: './ivr-option-table-editor.component.html',
  styleUrls: ['./ivr-option-table-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonModule,
    CommonModule,
    DataTableModule,
    DynamicDialogModule,
    MatIconModule,
    MessagesModule,
    ProgressSpinnerModule,
    SharedModule,
    TooltipModule,
    TranslocoModule,
  ],
  providers: [DialogService],
})
export class IVROptionTableEditorComponent implements OnInit {
  //////////////////////////////////////////////////////////////////////////////////////////
  // Services
  //////////////////////////////////////////////////////////////////////////////////////////

  private readonly dialogConfig = inject(DynamicDialogConfig);

  private readonly dialogService = inject(DialogService);

  private readonly ivrService = inject(IvrService);

  private readonly messageService = inject(MessageService);

  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  //////////////////////////////////////////////////////////////////////////////////////////
  // Variables
  //////////////////////////////////////////////////////////////////////////////////////////

  public ivr: IVR | undefined;

  public loading = true;

  public readonly cols = makePTableCols({
    columns: ['displayName', 'description', 'keyPresses', 'phrases', 'summary', 'action'],
    translationScope: 'ivr-option-table-editor',
  });

  public readonly tableRows$ = new BehaviorSubject<IVROptionEditorRow[]>([]);

  //////////////////////////////////////////////////////////////////////////////////////////
  // Lifecycle
  //////////////////////////////////////////////////////////////////////////////////////////

  public ngOnInit() {
    this.loadData();
  }

  private loadData() {
    const ivrId = this.dialogConfig.data?.ivrId;
    if (!ivrId) {
      this.loading = false;
      this.changeDetectorRef.detectChanges();
      return;
    }

    this.ivrService
      .getDoc(ivrId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (ivr) => {
          this.ivr = ivr;
          this.updateTableRows();
          this.loading = false;
          this.changeDetectorRef.detectChanges();
        },
        error: (error) => {
          console.error('Error loading IVR:', error);
          this.loading = false;
          this.changeDetectorRef.detectChanges();
        },
      });
  }

  //////////////////////////////////////////////////////////////////////////////////////////
  // Table Data
  //////////////////////////////////////////////////////////////////////////////////////////

  private updateTableRows() {
    if (!this.ivr) return;

    const options = this.ivr.getOptions();
    const rows = options.map((option, index) => createIVROptionEditorRow(option, index));
    this.tableRows$.next(rows);
  }

  //////////////////////////////////////////////////////////////////////////////////////////
  // Edit Action
  //////////////////////////////////////////////////////////////////////////////////////////

  public onEditOptionClicked(rowData: IVROptionEditorRow) {
    if (!this.ivr) return;

    const originalIVROption = rowData.ivrOption;
    const originalName = originalIVROption.getDisplayName().trim();
    const truncatedName = originalName.length > 50 ? `${originalName.substring(0, 50)}...` : originalName;

    const dialog = this.dialogService.open(IVROptionEditorComponent, {
      header: translate('ivr-option-table-editor.editDialogHeader', { name: truncatedName }),
      width: 'clamp(32rem, 95%, 80rem)',
      height: '90vh',
      closeOnEscape: true,
      dismissableMask: true,
      baseZIndex: 1000,
      autoZIndex: false,
      data: {
        ivrOption: originalIVROption,
        ivr: this.ivr,
        optionIndex: rowData.optionIndex,
      },
    });

    dialog.onClose.pipe(filter(Boolean), untilDestroyed(this)).subscribe(() => {
      this.messageService.add({
        severity: 'info',
        summary: translate('ivr-option-table-editor.editSuccessTitle'),
        detail: translate('ivr-option-table-editor.editSuccessBody', { name: originalName }),
        life: 5000,
      });
      this.loadData();
    });
  }

  //////////////////////////////////////////////////////////////////////////////////////////
  // Clone Action
  //////////////////////////////////////////////////////////////////////////////////////////

  public onCloneOptionClicked(rowData: IVROptionEditorRow) {
    const optionName = rowData.ivrOption.getDisplayName().trim();

    this.openConfirmationDialog({
      title: translate('ivr-option-table-editor.confirmCloneTitle'),
      body: translate('ivr-option-table-editor.confirmCloneBody'),
      requiredConfirmationString: optionName,
      onConfirm: () => this.cloneOption(rowData),
    });
  }

  private async cloneOption(rowData: IVROptionEditorRow): Promise<void> {
    if (!this.ivr) return;

    const originalOption = rowData.ivrOption;
    const originalName = originalOption.getDisplayName().trim();
    const clonedName = `Copy of ${originalName}`;

    const clonedOption = new IVROption({
      displayName: clonedName,
      description: originalOption.getDescription(),
      keyPresses: originalOption.getKeyPresses(),
      phrases: originalOption.getPhrases(),
      commands: originalOption.getCommands(),
    });

    const updatedOptions = [...this.ivr.getOptions(), clonedOption];
    this.ivr.setOptions(updatedOptions);

    const updateSucceeded = await this.ivrService.uploadIfNotEditingStaleObject(this.ivr);

    if (!updateSucceeded) {
      this.messageService.add({
        severity: 'error',
        summary: translate('ivr-option-table-editor.updateFailedTitle'),
        detail: translate('ivr-option-table-editor.updateFailedBody'),
        life: 5000,
      });
      return;
    }

    this.messageService.add({
      severity: 'info',
      summary: translate('ivr-option-table-editor.cloneSuccessTitle'),
      detail: translate('ivr-option-table-editor.cloneSuccessBody', { name: originalName, clonedName }),
      life: 5000,
    });

    this.updateTableRows();
    this.changeDetectorRef.detectChanges();
  }

  //////////////////////////////////////////////////////////////////////////////////////////
  // Delete Action
  //////////////////////////////////////////////////////////////////////////////////////////

  public onDeleteOptionClicked(rowData: IVROptionEditorRow) {
    const optionName = rowData.ivrOption.getDisplayName().trim();

    this.openConfirmationDialog({
      title: translate('ivr-option-table-editor.confirmDeleteTitle'),
      body: translate('ivr-option-table-editor.confirmDeleteBody'),
      requiredConfirmationString: optionName,
      onConfirm: () => this.deleteOption(rowData),
    });
  }

  private async deleteOption(rowData: IVROptionEditorRow): Promise<void> {
    if (!this.ivr) return;

    const optionName = rowData.ivrOption.getDisplayName().trim();
    const updatedOptions = this.ivr.getOptions().filter((_, index) => index !== rowData.optionIndex);
    this.ivr.setOptions(updatedOptions);

    const updateSucceeded = await this.ivrService.uploadIfNotEditingStaleObject(this.ivr);

    if (!updateSucceeded) {
      this.messageService.add({
        severity: 'error',
        summary: translate('ivr-option-table-editor.updateFailedTitle'),
        detail: translate('ivr-option-table-editor.updateFailedBody'),
        life: 5000,
      });
      return;
    }

    this.messageService.add({
      severity: 'info',
      summary: translate('ivr-option-table-editor.deleteSuccessTitle'),
      detail: translate('ivr-option-table-editor.deleteSuccessBody', { name: optionName }),
      life: 5000,
    });

    this.updateTableRows();
    this.changeDetectorRef.detectChanges();
  }

  //////////////////////////////////////////////////////////////////////////////////////////
  // Dialog Helper
  //////////////////////////////////////////////////////////////////////////////////////////

  private openConfirmationDialog(params: {
    title: string;
    body: string;
    requiredConfirmationString: string;
    onConfirm: () => Promise<void>;
  }) {
    this.dialogService
      .open(ConfirmWithInputComponent, {
        header: params.title,
        width: '30%',
        data: {
          title: params.title,
          body: params.body,
          requiredConfirmationString: params.requiredConfirmationString,
        },
      })
      .onClose.pipe(filter(Boolean), untilDestroyed(this))
      .subscribe(() => {
        params.onConfirm().catch((error) => console.error('Error executing action:', error));
      });
  }
}
